import React, { FunctionComponent, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import { Text, Icon, useIsBreakpointLargeAndUp, Divider } from '@afterpaytouch/core'
import { useAppRouter, useConsumerLendingEligible, useCountry } from '../../state'
import { Country } from '@afterpay/types'
import { Route, usePostLoginRoute } from '../../config/router'
import styles from './style.module.css'
import { BASE_PATH } from '../../env'
import { PayMonthlyInformationPoint } from './PayMonthlyInformationPoint/PayMonthlyInformationPoint'
import { PayMonthlyAccordion } from './PayMonthlyAccordion/PayMonthlyAccordion'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { PayMonthlyButton } from './PayMonthlyButton/PayMonthlyButton'

export const PayMonthly: FunctionComponent = () => {
  const { t } = useTranslation()
  const country = useCountry()
  const { prefetch, push } = useAppRouter()
  const consumerLendingEligible = useConsumerLendingEligible()
  const postLoginRoute = usePostLoginRoute()
  const isDesktop = useIsBreakpointLargeAndUp()
  const { logEvent } = useMonitoringWithEnduringEventProperties()

  const [hasSentAnalyticsData, setHasSentAnalyticsData] = useState({
    commonQuestion1: false,
    commonQuestion2: false,
    commonQuestion3: false,
    commonQuestion4: false,
    commonQuestion5: false,
  })

  useEffect(() => {
    prefetch(Route.INITIATE_BANK_VERIFICATION)
  }, [])

  useEffect(() => {
    if (!consumerLendingEligible || country !== Country.AU) {
      push(postLoginRoute)
    }
  }, [country, consumerLendingEligible])

  const headerSvgSrc = [BASE_PATH, 'images/pay-monthly-scene.svg'].join('/')

  const handleAccordionChange = useCallback(
    (question: string, title: string): void => {
      if (!hasSentAnalyticsData[question]) {
        logEvent(TrackingEvent.PRESSED_CL_MARKETING_PAGE_COMMON_QUESTIONS, { questionTitle: title })
        setHasSentAnalyticsData({ ...hasSentAnalyticsData, [question]: true })
      }
    },
    [hasSentAnalyticsData]
  )

  return (
    <>
      <div className={isDesktop ? styles.payMonthlyWrapperDesktop : styles.payMonthlyWrapperMobile}>
        <div className={styles.heroImage}>
          <Image
            data-testid='pay-monthly-scene'
            src={headerSvgSrc}
            alt=''
            width={344}
            height={142.34}
            style={{
              maxWidth: '100%',
              height: 'auto',
            }}
          />
        </div>

        <div className={styles.payMonthlyBottomContainer}>
          <div className={styles.bottomContainerPaddingTop}>
            <div className={styles.header}>
              <Text bold size={'XXL'}>
                {t('consumerLending:payMonthly:title1')}
              </Text>
              <Text bold size={'XXL'}>
                {t('consumerLending:payMonthly:title2')}
              </Text>
            </div>
            <div className={styles.secondaryHeader}>
              <Text>{t('consumerLending:payMonthly:subtitle')}</Text>
            </div>

            <div>
              <PayMonthlyInformationPoint
                headerText={t('consumerLending:payMonthly:information:header1')}
                contentText={t('consumerLending:payMonthly:information:content1')}
                InformationIcon={Icon.Calendar}
              />

              <PayMonthlyInformationPoint
                headerText={t('consumerLending:payMonthly:information:header2')}
                contentText={t('consumerLending:payMonthly:information:content2')}
                InformationIcon={Icon.Dollar}
              />

              <PayMonthlyInformationPoint
                headerText={t('consumerLending:payMonthly:information:header3')}
                contentText={t('consumerLending:payMonthly:information:content3')}
                InformationIcon={Icon.PasswordHide}
              />
            </div>
          </div>

          <Divider kind='Wide' />

          <div className={styles.bottomContainerPaddingBottom}>
            <div className={styles.faqSection}>
              <Text size='M' color='Gray50'>
                {t('consumerLending:payMonthly:faq:title')}
              </Text>
              <PayMonthlyAccordion
                headerText={t('consumerLending:payMonthly:faq:header1')}
                contentText={t('consumerLending:payMonthly:faq:content1')}
                onAccordionChange={() => handleAccordionChange('commonQuestion1', t('consumerLending:payMonthly:faq:header1'))}
              />
              <Divider kind='Hairline' />
              <PayMonthlyAccordion
                headerText={t('consumerLending:payMonthly:faq:header2')}
                contentText={t('consumerLending:payMonthly:faq:content2')}
                onAccordionChange={() => handleAccordionChange('commonQuestion2', t('consumerLending:payMonthly:faq:header2'))}
              />
              <Divider kind='Hairline' />
              <PayMonthlyAccordion
                headerText={t('consumerLending:payMonthly:faq:header3')}
                contentText={t('consumerLending:payMonthly:faq:content3')}
                onAccordionChange={() => handleAccordionChange('commonQuestion3', t('consumerLending:payMonthly:faq:header3'))}
              />
              <Divider kind='Hairline' />
              <PayMonthlyAccordion
                headerText={t('consumerLending:payMonthly:faq:header4')}
                contentText={t('consumerLending:payMonthly:faq:content4')}
                onAccordionChange={() => handleAccordionChange('commonQuestion4', t('consumerLending:payMonthly:faq:header4'))}
              />
              <Divider kind='Hairline' />
              <PayMonthlyAccordion
                headerText={t('consumerLending:payMonthly:faq:header5')}
                contentText={t('consumerLending:payMonthly:faq:content5')}
                onAccordionChange={() => handleAccordionChange('commonQuestion5', t('consumerLending:payMonthly:faq:header5'))}
              />
              <Divider kind='Hairline' />
            </div>
          </div>
        </div>
      </div>
      <PayMonthlyButton />
    </>
  )
}
